import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { TranslationService } from '../../../../services/translation.service'
import { CommonModule } from '@angular/common'
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator'
import { MatTableModule, MatTableDataSource } from '@angular/material/table'
import { MatButtonModule } from '@angular/material/button'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatIcon } from '@angular/material/icon'
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar'
import { FprReport } from '../../../../types/reports'
import { RouteService } from '../../../../services/route.service'
import { ROUTES } from '../../../../constants/routes'
import { ReportService } from '../../../../services/report.service'
import { SpinnerService } from '../../../../services/spinner.service'
import { DialogService } from '../../../../services/dialog.service'
import { ErrorService } from '../../../../services/error.service'
import { Dialog } from '@angular/cdk/dialog'
import { firstValueFrom } from 'rxjs/internal/firstValueFrom'
import { ApiError } from '../../../../types/apiError'
import { Router } from '@angular/router'
import { REPORT_STATUS_PUBLISHED } from '../../../../constants/reports'
import { SNACKBAR_DEFAULT_DURATION_MS } from '../../../../constants/common'

@Component({
  selector: 'app-reports-list',
  imports: [
    MatTableModule,
    MatPaginatorModule,
    CommonModule,
    MatButtonModule,
    MatTooltipModule,
    MatIcon,
    MatSnackBarModule,
  ],
  templateUrl: './reports-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './reports-list.component.scss',
})
export class ReportsListComponent implements OnInit, AfterViewInit {
  private routeService = inject(RouteService)
  translationService = inject(TranslationService)
  private reportService = inject(ReportService)
  private spinnerService = inject(SpinnerService)
  private dialogService = inject(DialogService)
  private errorService = inject(ErrorService)
  private dialog = inject(Dialog)
  private snackBar = inject(MatSnackBar)
  private router = inject(Router)

  reports: Array<FprReport> = []
  dataSource = new MatTableDataSource<FprReport>([])
  displayedColumns: string[] = [
    'name',
    'year',
    'status',
    'tags',
    'createdAt',
    'lastEditedAt',
    'publishedAt',
    'actions',
  ]

  publishedStatus: string = REPORT_STATUS_PUBLISHED

  @ViewChild(MatPaginator) paginator!: MatPaginator

  async ngOnInit() {
    const currentRoute = this.routeService.getUserRoutes(
      ROUTES.VITE_MARKETPLACE.title,
      `/admin/${ROUTES.REPORTS.path}`,
    )
    this.routeService.setRoute(currentRoute)

    try {
      this.spinnerService.startSpinner()
      this.reports = await firstValueFrom(this.reportService.getAll())
      this.dataSource.data = this.reports
      this.dataSource.paginator = this.paginator
      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator
  }

  getReportTagsText(tags: Array<string> | null | undefined): string {
    if (!tags?.length) {
      return ''
    }

    return tags.join(', ')
  }

  onEdit(report: FprReport) {
    if (report.status === 'in-progress') {
      this.router.navigate([
        `/admin/${ROUTES.REPORTS.path}/fpr/${report.id}/edit`,
      ])
    }
  }

  /**
   * Downloads the published XLSX report
   *
   * @param {string} reportId   The ID of the report to download.
   * @param {string} reportName A fallback name to use for the file if the server does not specify one.
   */
  async onDownloadPublishedReport(
    reportId: string,
    reportName: string,
  ): Promise<void> {
    if (!reportId) return

    try {
      this.spinnerService.startSpinner()
      const response = await firstValueFrom(
        this.reportService.downloadReport(reportId),
      )
      const filename = reportName

      // Create a temporary object URL for the Blob so we can prompt the browser to download it.
      const url = window.URL.createObjectURL(response.body!)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      // Revoke the object URL to avoid leaking memory once the download has been initiated.
      window.URL.revokeObjectURL(url)
    } catch (err) {
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    } finally {
      this.spinnerService.stopSpinner()
    }
  }

  /**
   * Copies the specified report and opens the newly created copy in edit mode.
   *
   * @param {FprReport} report The report object to be copied.
   * @returns {Promise<void>}
   */
  async onCopyModify(report: FprReport): Promise<void> {
    if (!report?.id) return

    try {
      this.spinnerService.startSpinner()
      const res = await firstValueFrom(
        this.reportService.cloneReport(report.id),
      )
      this.spinnerService.stopSpinner()

      const newName = res.name
      this.snackBar.open(
        this.translationService.getReportCopiedSuccessText(newName),
        '',
        { duration: SNACKBAR_DEFAULT_DURATION_MS },
      )
      this.router.navigate([`/admin/${ROUTES.REPORTS.path}/fpr/${res.id}/edit`])
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Downloads the raw data workbook for a report.
   */
  async onDownloadRawData(reportId: string, reportName: string): Promise<void> {
    if (!reportId) return

    try {
      this.spinnerService.startSpinner()

      const res = await firstValueFrom(this.reportService.downloadRaw(reportId))

      const blob = res.body!
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const ext = reportName.endsWith('.xlsx') ? '.xlsx' : ''
      const base = ext ? reportName.slice(0, -ext.length) : reportName
      a.download = `${base}_raw${ext || '.xlsx'}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)

      this.snackBar.open(this.translationService.getDownloadStartedText(), '', {
        duration: SNACKBAR_DEFAULT_DURATION_MS,
      })
    } catch (err) {
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    } finally {
      this.spinnerService.stopSpinner()
    }
  }
}
